/**
 * As reuniões e as atas na área do cliente — o que é dele, e nada além.
 *
 * **A tela é a SEGUNDA trava, e por isso ela filtra o que o servidor já filtrou.** O recorte de
 * verdade é do servidor: `visibilidade.py` decide o público e, antes dele, a policy
 * `ata_item_isolamento` da `0013` faz uma nota interna **não existir** para a sessão de um
 * cliente — em listagem, em detalhe, em `count(*)`, em campo derivado e no pacote de Kick Off.
 * Aqui a ata só é pedida quando o servidor disse que existe uma **publicada** para este acesso, só
 * é desenhada se vier publicada, e cada item passa de novo pelo mesmo predicado. A spec §3 chama
 * a RLS de camada externa, não de camada única: as travas dizem a mesma coisa de propósito, e
 * quando uma falha a outra ainda segura.
 *
 * **Reunião sem ata publicada aparece como reunião, e nada mais.** Nem "ata em preparação", nem
 * um aviso de que existe algo escrito: contar isso já é metade do vazamento — diz que houve
 * reunião, que alguém escreveu sobre ela e que o texto ainda está mudando do lado de dentro. Para
 * o cliente, uma reunião com ata não publicada responde exatamente como uma reunião sem ata.
 *
 * **A gravação tem endereço próprio.** A Central de Materiais lista as gravações concluídas das
 * reuniões visíveis; esta página continua focada no registro e na ata. Áudio e transcrição são
 * matéria-prima interna e não atravessam a policy.
 *
 * **E agendar também não tem lugar aqui** — desde 01/09/2026, por decisão da consultoria. A
 * reunião saiu da esteira: não é etapa, não é obrigatória, e quem marca é a equipe. A página ficou
 * e mudou de nome porque o que ela sempre entregou de verdade era o registro. O cliente perdeu o
 * `reuniao:agendar` na `0019`, então a ausência de botão aqui não é a única trava: o servidor
 * recusa o ato mesmo que alguém chegue à rota por fora.
 */

import { Link } from "react-router";
import { Aviso, Cartao, Chip } from "../componentes/ui";
import { AtaPublicada, PendenciasDaAta } from "../componentes/reuniao";
import { useTitulo } from "../nucleo/titulo";
import { useImplantacaoDoCliente } from "../nucleo/implantacoes";
import { mensagemDoConsole } from "../nucleo/console";
import {
  formatarDuracao,
  formatarQuando,
  registrarEntradaNaReuniao,
  rotuloDoEstadoDaReuniao,
  rotuloDoTipoDeReuniao,
  useAtaDaReuniao,
  usePendenciasDaAta,
  useReunioes,
  type Reuniao,
} from "../nucleo/reuniao";

export function ReunioesDoCliente() {
  useTitulo("Agenda");
  /* A consulta não tem `esteira:ver`: a implantação dela vem do kickoff (ver o hook). */
  const { pendente, erro, implantacaoId, acessoDeConsulta, semAcesso } =
    useImplantacaoDoCliente();
  const reunioes = useReunioes(implantacaoId);
  const pendencias = usePendenciasDaAta(implantacaoId, implantacaoId.length > 0);

  return (
    <>
      <h1 className="titulo-pagina">Agenda</h1>
      <p className="lead">
        As próximas conversas com a GEOvendas e o histórico do que já aconteceu, com atas e
        decisões publicadas para você.
      </p>

      {pendente ? (
        <p className="lead" role="status">
          Carregando…
        </p>
      ) : semAcesso ? (
        <Aviso tom="marca" data-testid="agenda-fora-do-acesso">
          <b>O seu acesso é de consulta.</b> Ele não inclui a agenda da implantação. Se você
          precisa dela, peça a quem administra o acesso da sua empresa.
        </Aviso>
      ) : erro ? (
        <Aviso tom="erro" data-testid="falha-da-esteira-do-cliente">
          {mensagemDoConsole(erro)}
        </Aviso>
      ) : implantacaoId === "" ? (
        <Aviso tom="marca" data-testid="sem-implantacao-para-reunioes">
          Assim que a sua implantação for aberta, as reuniões marcadas com você aparecem aqui.
        </Aviso>
      ) : reunioes.isPending ? (
        <p className="lead" role="status">
          Carregando as reuniões…
        </p>
      ) : reunioes.isError ? (
        <Aviso tom="erro" data-testid="falha-das-reunioes-do-cliente">
          {mensagemDoConsole(reunioes.error)}
        </Aviso>
      ) : reunioes.data.total === 0 ? (
        <Aviso tom="marca" data-testid="sem-reunioes-do-cliente">
          <b>Nenhuma reunião marcada até agora.</b> Quando o consultor registrar a primeira, o
          horário e o link aparecem aqui — e a ata, depois que ela for publicada.
        </Aviso>
      ) : (
        <>
          {acessoDeConsulta ? (
            <Aviso tom="marca" className="mb-4" data-testid="agenda-acesso-de-consulta">
              <b>O seu acesso é de consulta.</b> Você vê as reuniões e o que ficou combinado, mas
              não o andamento das etapas.
            </Aviso>
          ) : null}
          <Agenda reunioes={reunioes.data.reunioes} />
        </>
      )}

      {pendencias.isError ? (
        /* Sem isto a seção sumia como se não houvesse combinado nenhum: a falha vira aviso. */
        <section className="mt-6" aria-labelledby="titulo-das-minhas-pendencias">
          <h2 className="titulo-secao" id="titulo-das-minhas-pendencias">
            O que ficou combinado
          </h2>
          <Aviso tom="erro" data-testid="falha-das-pendencias-do-cliente">
            Não foi possível carregar o que ficou combinado. {mensagemDoConsole(pendencias.error)}
          </Aviso>
        </section>
      ) : pendencias.data && pendencias.data.total > 0 ? (
        <section className="mt-6" aria-labelledby="titulo-das-minhas-pendencias">
          <h2 className="titulo-secao" id="titulo-das-minhas-pendencias">
            O que ficou combinado
          </h2>
          <PendenciasDaAta pendencias={pendencias.data} publico="cliente" />
        </section>
      ) : null}

      <p className="nota-rodape mt-5">
        <Link to="/app">Voltar à minha implantação</Link>
      </p>
    </>
  );
}

function Agenda({ reunioes }: { reunioes: Reuniao[] }) {
  const proximas = reunioes.filter((reuniao) => reuniao.estado === "agendada");
  const historico = [...reunioes]
    .filter((reuniao) => reuniao.estado !== "agendada")
    .reverse();

  return (
    <div className="lista-de-itens" data-testid="reunioes-do-cliente">
      <section aria-labelledby="titulo-das-proximas-reunioes" data-testid="proximas-reunioes">
        <h2 className="titulo-secao" id="titulo-das-proximas-reunioes">
          Próximas reuniões
        </h2>
        {proximas.length > 0 ? (
          proximas.map((reuniao) => <ReuniaoDoCliente key={reuniao.id} reuniao={reuniao} />)
        ) : (
          <p className="cartao__texto">Nenhuma reunião marcada.</p>
        )}
      </section>

      <section aria-labelledby="titulo-do-historico-da-agenda" data-testid="historico-da-agenda">
        <h2 className="titulo-secao" id="titulo-do-historico-da-agenda">
          Histórico
        </h2>
        {historico.length > 0 ? (
          historico.map((reuniao) => <ReuniaoDoCliente key={reuniao.id} reuniao={reuniao} />)
        ) : (
          <p className="cartao__texto">Nenhuma reunião no histórico.</p>
        )}
      </section>
    </div>
  );
}

function ReuniaoDoCliente({ reuniao }: { reuniao: Reuniao }) {
  /* A ata só é pedida quando o servidor já disse que existe uma publicada PARA ESTE ACESSO —
     `ata_publicada` chega recortado. Não é a tela que decide o que perguntar, e por isso ela
     nunca pede o que levaria `404`. */
  const ata = useAtaDaReuniao(reuniao.id, reuniao.ata_publicada);
  const titulo = `${rotuloDoTipoDeReuniao(reuniao.tipo)} · ${
    formatarQuando(reuniao.agendada_para) ?? "—"
  }`;

  // A segunda trava também aqui: só desenha o que veio publicado.
  if (ata.data?.publicada) {
    return <AtaPublicada ata={ata.data} titulo={titulo} data-testid={`ata-${reuniao.id}`} />;
  }

  return (
    <Cartao
      titulo={titulo}
      data-testid={`reuniao-${reuniao.id}`}
      acessorio={
        <Chip
          estado={
            reuniao.estado === "realizada"
              ? "ok"
              : reuniao.estado === "cancelada"
                ? "neutro"
                : "aguardando"
          }
        >
          {rotuloDoEstadoDaReuniao(reuniao.estado)}
        </Chip>
      }
    >
      <p className="cartao__texto">
        {formatarDuracao(reuniao.duracao_min)}
        {reuniao.link ? (
          <>
            {" · "}
            <a
              href={reuniao.link}
              target="_blank"
              rel="noreferrer noopener"
              onClick={() => registrarEntradaNaReuniao(reuniao.id)}
            >
              Entrar na reunião
            </a>
          </>
        ) : null}
      </p>
      <p className="nota-rodape mt-2">
        {reuniao.estado === "cancelada"
          ? "Esta reunião foi cancelada."
          : "A ata desta reunião aparece aqui assim que for publicada."}
      </p>
    </Cartao>
  );
}
